import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ProgressBar } from "react-bootstrap";
import * as apiHelper from "../../../Services/ApiHelper";
import * as toast from "../../../Wrapper/toast/toast";
import IconResource from "../../../Wrapper/IconResource/IconResource";
import Button from "../../_widgets/Button/Button";

/**
 * Pre-flight + progress dialog for "Download" on the Track Patient page.
 *
 * Why a dialog instead of a one-shot button click:
 *  1. **Visibility into cost.** A naive click was triggering a 30-second
 *     server job that PHP-FPM was killing mid-flight (manifesting as a
 *     spurious "CORS error"). Showing the user the row count + estimated
 *     time up-front lets them either narrow filters or pick a faster
 *     format before incurring the cost.
 *  2. **Format choice.** PDF is rich but slow; CSV (Excel-compatible) is
 *     near-instant for the same dataset. The dialog exposes both.
 *  3. **Real progress.** Once the download starts, axios's
 *     `onDownloadProgress` drives a determinate bar so the user knows the
 *     request is alive instead of staring at an empty button.
 *
 * Lifecycle:
 *   open → fetch /estimate → pick format → start download (with progress)
 *      → save file → close.
 *   At any "in-flight" stage Cancel aborts via AbortController.
 */
const DownloadPatientsModal = ({ show, onClose, buildFilterPayload }) => {
  const [estimate, setEstimate] = useState(null);
  const [estimateError, setEstimateError] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [format, setFormat] = useState("csv");
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [indeterminate, setIndeterminate] = useState(false);

  const abortRef = useRef(null);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;

    setEstimate(null);
    setEstimateError(null);
    setEstimateLoading(true);
    setProgress(0);
    setIndeterminate(false);
    setDownloading(false);
    // Default to Excel (XLSX) — most familiar to users coming from the
    // existing reports flow. CSV stays available for huge exports above
    // the XLSX cap.
    setFormat("xlsx");

    apiHelper
      .getTrackPatientEstimate(buildFilterPayload())
      .then((res) => {
        if (res?.isSuccess) {
          setEstimate(res.data);
        } else {
          setEstimateError(res?.message || "Could not fetch record count");
        }
      })
      .catch(() => setEstimateError("Could not fetch record count"))
      .finally(() => setEstimateLoading(false));

    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [show, buildFilterPayload]);

  // Per-format meta from the BE: { rows, capped, cap, estimated_seconds }
  // Resolved here once instead of indexing `estimate.formats[format]` at
  // every read site.
  const selectedFormatMeta = useMemo(() => {
    return estimate?.formats?.[format] ?? null;
  }, [estimate, format]);

  const totalForFormat = useMemo(
    () => selectedFormatMeta?.estimated_seconds ?? 1,
    [selectedFormatMeta],
  );

  /**
   * Time-driven progress fallback.
   *
   * mPDF buffers the entire response and flushes at the end, so axios
   * `onDownloadProgress` only fires once near 100%. If we relied solely on
   * it the bar would sit at 0% for ~25s and then snap to 100% — worse than
   * no bar. We therefore advance progress linearly from 0 → 90% across the
   * estimated duration, capping at 90% until the real response lands and
   * snaps to 100%. CSV is fast enough that the timer rarely matters.
   */
  const startTimedProgress = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return;
    const startedAt = Date.now();
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const next = Math.min(0.9, elapsed / totalSeconds);
      setProgress((prev) => (next > prev ? next : prev));
    }, 250);
  };

  const stopTimedProgress = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  /**
   * Build a `patient-list-YYYYMMDD-HHMM` stem in local time. Used as a
   * fallback when the server-side filename isn't reachable from JS — most
   * commonly because CORS hasn't exposed `Content-Disposition` yet, in
   * which case axios sees no header and we'd otherwise hand the browser an
   * empty `download` attribute (browsers then guess from MIME → "download.xlsx").
   */
  const buildClientFilename = (fmt) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp =
      now.getFullYear() +
      pad(now.getMonth() + 1) +
      pad(now.getDate()) +
      "-" +
      pad(now.getHours()) +
      pad(now.getMinutes());
    return `patient-list-${stamp}.${fmt}`;
  };

  const triggerSave = (blob, filename, fmt) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || buildClientFilename(fmt);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleStartDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setProgress(0);
    setIndeterminate(false);

    abortRef.current = new AbortController();
    startTimedProgress(totalForFormat);

    const onProgress = (frac) => {
      if (frac === null) {
        setIndeterminate(true);
        return;
      }
      setProgress((prev) => (frac > prev ? frac : prev));
    };

    try {
      // Format → API helper. Centralized so adding a future format only
      // touches this map plus the per-format radio rendering below.
      const downloadFn = {
        xlsx: apiHelper.downloadTrackPatientXlsx,
        csv: apiHelper.downloadTrackPatientCsv,
        pdf: apiHelper.downloadTrackPatientPdf,
      }[format];
      const { blob, filename } = await downloadFn(buildFilterPayload(), {
        onProgress,
        signal: abortRef.current.signal,
      });
      stopTimedProgress();
      setProgress(1);
      triggerSave(blob, filename, format);
      onClose();
    } catch (err) {
      stopTimedProgress();
      if (err?.name === "CanceledError" || err?.name === "AbortError") {
        toast.warn("Download cancelled");
      } else {
        toast.error(
          err?.message || "Failed to download. Try a smaller filter range or CSV format.",
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
    stopTimedProgress();
    if (!downloading) onClose();
  };

  const formatNumber = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);

  return (
    <Modal show={show} onHide={handleCancel} centered backdrop="static" size="md">
      <Modal.Header closeButton>
        <Modal.Title>Download Patient Records</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {estimateLoading ? (
          <div className="text-center py-4 text-muted">
            <div className="mb-2">
              <IconResource type="loader" size={20} />
            </div>
            Calculating record count…
          </div>
        ) : estimateError ? (
          <div className="text-danger small">{estimateError}</div>
        ) : estimate ? (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Total matching records</span>
                <strong style={{ fontSize: 18 }}>{formatNumber(estimate.count)}</strong>
              </div>
              {/* CSV streams uncapped on the BE, XLSX and PDF are capped.
                  The banner only fires for the *currently selected* format
                  when its cap is hit, and nudges the user toward CSV. */}
              {selectedFormatMeta?.capped && (
                <div
                  className="mt-2 p-2 small"
                  style={{
                    background: "#fff5e6",
                    border: "1px solid #ffb84d",
                    borderRadius: 4,
                    color: "#8a4f00",
                  }}
                >
                  {format.toUpperCase()} includes only the first{" "}
                  {formatNumber(selectedFormatMeta.cap)} rows. Switch to CSV to export all{" "}
                  {formatNumber(estimate.count)} records.
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="text-muted small mb-2">Choose format</div>
              <FormatOption
                value="xlsx"
                checked={format === "xlsx"}
                disabled={downloading}
                onChange={setFormat}
                title="Excel (.xlsx)"
                meta={`${formatNumber(estimate.formats?.xlsx?.rows ?? 0)} rows${
                  estimate.formats?.xlsx?.capped
                    ? ` (capped at ${formatNumber(estimate.formats.xlsx.cap)})`
                    : ""
                } · ~${estimate.formats?.xlsx?.estimated_seconds ?? 4}s · native Excel format`}
                recommended
              />
              <FormatOption
                value="csv"
                checked={format === "csv"}
                disabled={downloading}
                onChange={setFormat}
                title="CSV (Excel-compatible)"
                meta={`${formatNumber(estimate.formats?.csv?.rows ?? estimate.count)} rows · ~${
                  estimate.formats?.csv?.estimated_seconds ?? 1
                }s · opens in Excel / Google Sheets, no row limit`}
              />
              <FormatOption
                value="pdf"
                checked={format === "pdf"}
                disabled={downloading}
                onChange={setFormat}
                title="PDF"
                meta={`${formatNumber(estimate.formats?.pdf?.rows ?? 0)} rows${
                  estimate.formats?.pdf?.capped
                    ? ` (capped at ${formatNumber(estimate.formats.pdf.cap)})`
                    : ""
                } · ~${estimate.formats?.pdf?.estimated_seconds ?? 5}s · printable layout`}
              />
            </div>

            {downloading && (
              <div className="mt-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>{indeterminate ? "Generating file…" : "Downloading…"}</span>
                  {!indeterminate && <span>{Math.round(progress * 100)}%</span>}
                </div>
                <ProgressBar
                  now={indeterminate ? 100 : Math.round(progress * 100)}
                  animated={indeterminate}
                  striped={indeterminate}
                />
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button
          buttonType="secondary"
          text={downloading ? "Cancel" : "Close"}
          onClick={handleCancel}
        />
        <Button
          buttonType="primary"
          text={downloading ? "Downloading…" : "Start Download"}
          isLoading={downloading}
          disabled={
            estimateLoading || !!estimateError || !estimate || estimate.count === 0 || downloading
          }
          onClick={handleStartDownload}
        />
      </Modal.Footer>
    </Modal>
  );
};

const FormatOption = ({ value, checked, disabled, onChange, title, meta, recommended }) => (
  <label
    className="d-flex align-items-center gap-2 p-2 mb-2"
    style={{
      border: `1px solid ${checked ? "var(--primary, #4e73df)" : "#e3e6f0"}`,
      borderRadius: 6,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <input
      type="radio"
      name="dl-format"
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={() => onChange(value)}
    />
    <div className="flex-grow-1">
      <div className="d-flex align-items-center gap-2">
        <strong>{title}</strong>
        {recommended && (
          <span
            className="badge"
            style={{ background: "#e6f4ea", color: "#1e7e34", fontWeight: 500, fontSize: 10 }}
          >
            Recommended
          </span>
        )}
      </div>
      <div className="small text-muted">{meta}</div>
    </div>
  </label>
);

export default DownloadPatientsModal;
